import { useEffect, useRef, useState } from 'react';

const ValueProposition = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: 'ri-truck-line',
      color: '#00d4ff',
      title: 'Mobile LED Trucks',
      desc: 'High-resolution LED billboard trucks that hit the busiest streets, events, and hotspots across Tampa Bay.',
    },
    {
      icon: 'ri-eye-line',
      color: '#39ff14',
      title: 'High Visibility',
      desc: 'Impossible to ignore. Our vibrant displays capture attention day and night, making your brand unforgettable.',
    },
    {
      icon: 'ri-bar-chart-2-line',
      color: '#b026ff',
      title: '10,000+ Weekly Impressions',
      desc: 'Every campaign delivers thousands of real, local impressions — no bots, no skips, just eyes on your brand.',
    },
    {
      icon: 'ri-map-pin-2-line',
      color: '#ff073a',
      title: 'Targeted Tampa Routes',
      desc: 'We drive through your target neighborhoods, competitor zones, and high-traffic areas for maximum impact.',
    },
  ];

  const phrases = [
    'BEST PRICE PER VIEW IN MOBILE ADVERTISING',
    'TURN TRAFFIC INTO CUSTOMERS',
    'BE SEEN. BE REMEMBERED.',
    'YOUR BRAND ON THE MOVE',
    "WE DON'T WAIT FOR ATTENTION — WE DRIVE IT",
    'VISIBILITY THAT CONVERTS',
    'YOUR BRAND EVERYWHERE PEOPLE GO',
  ];

  const tickerContent = [...phrases, ...phrases];

  return (
    <section id="value" className="relative py-24 md:py-32 bg-dark-charcoal overflow-hidden" ref={sectionRef}>
      {/* Top Neon Streak */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, #00d4ff, #39ff14, #b026ff, transparent)' }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div
            className={`flex-1 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <div
              className="inline-block text-xs font-rajdhani font-bold tracking-widest uppercase mb-4 px-3 py-1 rounded-full"
              style={{ color: '#39ff14', border: '1px solid #39ff14', boxShadow: '0 0 10px rgba(57,255,20,0.3)' }}
            >
              Why MartLed
            </div>
            <h2 className="font-orbitron font-black text-3xl md:text-4xl lg:text-5xl leading-tight mb-8">
              <span className="text-white">MOBILE LED ADVERTISING</span>
              <br />
              <span style={{ color: '#00d4ff', textShadow: '0 0 20px #00d4ff' }}>THAT DOMINATES</span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg leading-relaxed mb-10 max-w-xl">
              In Tampa&apos;s crowded market, standing out isn&apos;t optional — it&apos;s survival. MartLed puts your
              brand on a moving billboard that travels directly to your customers, turning every mile into marketing gold.
            </p>

            {/* Feature Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {features.map((f, i) => (
                <div
                  key={f.title}
                  className={`group p-5 rounded-2xl bg-dark-card cursor-pointer transition-all duration-500 ${
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{
                    border: `1px solid ${f.color}40`,
                    transitionDelay: `${i * 100}ms`,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = f.color;
                    (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 20px ${f.color}40`;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = `${f.color}40`;
                    (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                  }}
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl mb-4" style={{ backgroundColor: `${f.color}15` }}>
                    <i className={`${f.icon} text-2xl`} style={{ color: f.color, textShadow: `0 0 10px ${f.color}` }} />
                  </div>
                  <h3 className="font-rajdhani font-bold text-lg text-white mb-2">{f.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Visual */}
          <div
            className={`flex-1 flex flex-col gap-6 transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div
              className="relative rounded-3xl overflow-hidden"
              style={{ border: '2px solid #00d4ff', boxShadow: '0 0 40px rgba(0,212,255,0.2)' }}
            >
              <img
                src="https://public.readdy.ai/ai/img_res/edited_d16a2d255c1439b68740ff5479351e60_035acd72.jpg"
                alt="MartLed truck in action"
                className="w-full h-64 md:h-80 object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

              {/* Floating Badges */}
              <div
                className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-rajdhani font-bold"
                style={{ backgroundColor: 'rgba(0,0,0,0.85)', color: '#39ff14', border: '1px solid #39ff14', boxShadow: '0 0 14px rgba(57,255,20,0.5)' }}
              >
                <i className="ri-eye-line mr-1" /> 10,000+ Weekly Views
              </div>
              <div
                className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full text-xs font-rajdhani font-bold"
                style={{ backgroundColor: 'rgba(0,0,0,0.8)', color: '#00d4ff', border: '1px solid #00d4ff', boxShadow: '0 0 10px rgba(0,212,255,0.3)' }}
              >
                <i className="ri-flashlight-line mr-1" /> Maximum Visibility
              </div>
            </div>

            {/* Extra Stats */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { val: '10K+', sub: 'Views/Week', color: '#39ff14' },
                { val: '5 Days', sub: 'Per Week', color: '#00d4ff' },
                { val: '#1', sub: 'In Tampa', color: '#ff073a' },
              ].map((s) => (
                <div
                  key={s.sub}
                  className="p-4 rounded-2xl bg-dark-card text-center"
                  style={{ border: `1px solid ${s.color}30` }}
                >
                  <div
                    className="font-orbitron font-black text-2xl"
                    style={{ color: s.color, textShadow: `0 0 15px ${s.color}` }}
                  >
                    {s.val}
                  </div>
                  <div className="text-gray-500 text-xs font-rajdhani tracking-wider uppercase mt-1">{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Ticker */}
      <div className="mt-16 overflow-hidden py-4 border-t border-b border-neon-green/10 bg-black/30">
        <div className="flex animate-scroll-left whitespace-nowrap">
          {tickerContent.map((phrase, i) => (
            <span key={i} className="inline-flex items-center gap-4 mr-12">
              <span
                className="font-rajdhani font-bold text-sm md:text-base tracking-widest"
                style={{ color: '#39ff14', textShadow: '0 0 10px #39ff14' }}
              >
                {phrase}
              </span>
              <span style={{ color: '#b026ff' }}>◆</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ValueProposition;
